import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { QrCode } from 'components';
import { TranslationNamespace } from 'i18n';

import { SBER_QR_PALETTE } from '../../../constants/sber-qr-palette.constant';
import type { SberQrCollapseState } from '../../../types';

type SberQrWrapperProps = {
  collapseState: SberQrCollapseState;
  hasQrSource: boolean;
  isLoadingQrSource?: boolean;
  qrReady: boolean;
  showReadyText: boolean;
  transferUrl: string | undefined;
  qrCodeUrl: string | undefined;
  onQrGenerated: (url: string) => void;
  onImageLoad: () => void;
};

export const SberQrWrapper: React.FC<SberQrWrapperProps> = ({
  collapseState,
  hasQrSource,
  isLoadingQrSource,
  qrReady,
  showReadyText,
  transferUrl,
  qrCodeUrl,
  onQrGenerated,
  onImageLoad,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.sber_qr',
  });

  return (
    <div
      className={cx('sber-qr-collapse', {
        'sber-qr-collapse--open': collapseState === 'open',
        'sber-qr-collapse--closing': collapseState === 'closing',
        'sber-qr-collapse--closed': collapseState === 'closed',
        'tw-mt-4 md:tw-mt-0':
          collapseState === 'auto' || collapseState === 'open',
      })}
    >
      <div className="tw-relative tw-w-full tw-max-w-[320px] md:tw-max-w-[360px] tw-aspect-square tw-mx-auto tw-p-[14px] tw-rounded-[32px] tw-bg-white tw-shadow-[0_15px_40px_rgba(0,0,0,.28)]">
        {(isLoadingQrSource || (hasQrSource && !qrReady)) && (
          <div className="tw-absolute tw-inset-0 tw-z-[2] tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-[#183128]">
            <div
              style={{ borderTopColor: '#18b875' }}
              className="tw-animate-spin tw-w-[44px] tw-h-[44px] tw-mb-[13px] tw-border-[3px] tw-border-solid tw-border-[rgba(7,49,31,.12)] tw-rounded-full"
            />
            <strong className="tw-text-[13px]">{t('loading_qr')}</strong>
          </div>
        )}
        {transferUrl ? (
          <QrCode
            data={transferUrl}
            className="tw-w-full tw-h-full"
            innerClassName="tw-w-full tw-h-full tw-border-0 tw-px-0"
            imageClassName="tw-w-full tw-h-full"
            onGenerated={onQrGenerated}
          />
        ) : qrCodeUrl ? (
          <img
            src={qrCodeUrl}
            alt={t('qr_ready_to_save')}
            onLoad={onImageLoad}
            style={{
              imageRendering: 'pixelated',
              visibility: qrReady ? 'visible' : 'hidden',
            }}
            className="tw-block tw-w-full tw-h-full"
          />
        ) : isLoadingQrSource ? null : (
          <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full tw-text-center tw-text-[#183128] tw-text-[13px] tw-font-bold tw-px-4">
            {t('qr_error')}
          </div>
        )}
      </div>

      {qrReady && showReadyText && (
        <p className="tw-flex tw-gap-2 tw-justify-center tw-items-center tw-my-[14px] tw-text-[#b9d2c7] tw-text-[12px] tw-font-bold">
          <span
            style={{
              background: SBER_QR_PALETTE.bright,
              boxShadow: `0 0 12px ${SBER_QR_PALETTE.green}`,
            }}
            className="tw-inline-block tw-w-[7px] tw-h-[7px] tw-rounded-full"
          />
          {t('qr_ready_to_scan')}
        </p>
      )}
      {!hasQrSource && !isLoadingQrSource && (
        <p className="tw-flex tw-gap-2 tw-justify-center tw-items-center tw-my-[14px] tw-text-[#b9d2c7] tw-text-[12px] tw-font-bold">
          <span className="tw-inline-block tw-w-[7px] tw-h-[7px] tw-rounded-full tw-bg-yellow-400" />
          {t('qr_error')}
        </p>
      )}
    </div>
  );
};
